import { useState, useEffect } from "react";
import Dropdown from "@/components/ui/DropDown";
import useCampaignDetailStore from "../store/useCampaignDetailStore";
import { ChevronDown, ChevronUp, FileText, Lightbulb, Copy, MessageCircle } from "lucide-react";

interface FilterState {
  status: string;
  language: string;
  targeting: string;
  category: string;
  publisher: string;
}

interface AssetRow {
  id: string;
  thumbnail: string;
  language: string;
  objective: string;
  region: string;
  category: string;
  publisher: string;
  adType: string;
  dueDate: string;
  statusIcons: StatusIcon[];
}

interface StatusIcon {
  type: "document" | "copy" | "lightbulb" | "question" | "comment";
  color: "yellow" | "orange";
  count?: number;
}

export default function CampaignDetailListView() {
  const { campaignDetail } = useCampaignDetailStore();
  const [expandedFlights, setExpandedFlights] = useState<Record<string, boolean>>({});

  const [filters, setFilters] = useState<FilterState>({
    status: "All",
    language: "All",
    targeting: "All",
    category: "All",
    publisher: "All",
  });

  // Extract filter options from campaign data
  const getFilterOptions = () => {
    if (!campaignDetail) return {
      statusOptions: [{ title: "All", value: "All" }],
      languageOptions: [{ title: "All", value: "All" }],
      targetingOptions: [{ title: "All", value: "All" }],
      categoryOptions: [{ title: "All", value: "All" }],
      publisherOptions: [{ title: "All", value: "All" }],
    };

    const languages = new Set<string>();
    const regions = new Set<string>();
    const categories = new Set<string>();

    campaignDetail.nested_by_flight?.forEach(flight => {
      flight.flight_rows?.forEach(row => {
        if (row?.language) languages.add(row.language);
        if (row?.region) regions.add(row.region);
        row.flight_ad_categories?.forEach(cat => {
          if (cat?.ad_category_name) categories.add(cat.ad_category_name);
        });
      });
    });

    return {
      statusOptions: [
        { title: "All", value: "All" },
        { title: "Briefed", value: "briefed" },
        { title: "In Progress", value: "in_progress" },
        { title: "In Review", value: "in_review" },
        { title: "Approved", value: "approved" },
      ],
      languageOptions: [
        { title: "All", value: "All" },
        ...Array.from(languages).map(lang => ({ title: lang, value: lang }))
      ],
      targetingOptions: [
        { title: "All", value: "All" },
        ...Array.from(regions).map(region => ({ title: region, value: region }))
      ],
      categoryOptions: [
        { title: "All", value: "All" },
        ...Array.from(categories).map(cat => ({ title: cat, value: cat }))
      ],
      publisherOptions: [
        { title: "All", value: "All" },
        { title: "Meta", value: "Meta" },
        { title: "Pinterest", value: "Pinterest" },
        { title: "Snapchat", value: "Snapchat" },
      ],
    };
  };

  const filterOptions = getFilterOptions();

  // Auto-expand all flights on load
  useEffect(() => {
    if (campaignDetail?.nested_by_flight) {
      const expanded: Record<string, boolean> = {};
      campaignDetail.nested_by_flight.forEach((_, index) => {
        expanded[`flight-${index}`] = true;
      });
      setExpandedFlights(expanded);
    }
  }, [campaignDetail]);

  const toggleFlight = (flightKey: string) => {
    setExpandedFlights(prev => ({
      ...prev,
      [flightKey]: !prev[flightKey],
    }));
  };

  // Transform campaign data into asset rows
  const getFlightAssets = (flightId: string): AssetRow[] => {
    const flight = campaignDetail?.nested_by_flight.find(f => f.flight_id === flightId);
    if (!flight) return [];

    const assets: AssetRow[] = [];

    flight.flight_rows.forEach(row => {
      row.flight_ad_categories.forEach(category => {
        category.ad_rows.forEach((ad, index) => {
          // Generate mock status icons based on ad type and category
          const statusIcons: StatusIcon[] = [];
          const iconCount = Math.floor(Math.random() * 5) + 1;

          for (let i = 0; i < iconCount; i++) {
            const types: StatusIcon['type'][] = ["document", "copy", "lightbulb", "question", "comment"];
            const colors: StatusIcon['color'][] = ["yellow", "orange"];
            statusIcons.push({
              type: types[Math.floor(Math.random() * types.length)],
              color: colors[Math.floor(Math.random() * colors.length)],
            });
          }

          assets.push({
            id: `${ad.asset_id}-${index}`,
            thumbnail: "https://via.placeholder.com/40",
            language: row.language,
            objective: row.objective,
            region: row.region || "N/A",
            category: category.ad_category_name || "Social",
            publisher: "Meta", // This would come from actual data
            adType: ad.ad_type,
            dueDate: flight.end_date,
            statusIcons,
          });
        });
      });
    });

    return assets;
  };

  // Apply filters to assets
  const filterAssets = (assets: AssetRow[]): AssetRow[] => {
    return assets.filter(asset => {
      if (filters.language !== "All" && asset.language !== filters.language) return false;
      if (filters.targeting !== "All" && asset.region !== filters.targeting) return false;
      if (filters.category !== "All" && asset.category !== filters.category) return false;
      if (filters.publisher !== "All" && asset.publisher !== filters.publisher) return false;
      return true;
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const getStatusIconComponent = (icon: StatusIcon, index: number) => {
    const colorClass = icon.color === "yellow" ? "bg-yellow-300" : "bg-orange-400";
    const iconSize = 16;

    switch (icon.type) {
      case "document":
        return (
          <div key={index} className={`${colorClass} rounded-full w-8 h-8 flex items-center justify-center`}>
            <FileText size={iconSize} className="text-gray-800" />
          </div>
        );
      case "copy":
        return (
          <div key={index} className={`${colorClass} rounded-full w-8 h-8 flex items-center justify-center`}>
            <Copy size={iconSize} className="text-gray-800" />
          </div>
        );
      case "lightbulb":
        return (
          <div key={index} className={`${colorClass} rounded-full w-8 h-8 flex items-center justify-center`}>
            <Lightbulb size={iconSize} className="text-gray-800" />
          </div>
        );
      case "question":
        return (
          <div key={index} className={`${colorClass} rounded-full w-8 h-8 flex items-center justify-center font-bold text-gray-800`}>
            ?
          </div>
        );
      case "comment":
        return (
          <div key={index} className={`bg-red-400 rounded-full w-8 h-8 flex items-center justify-center`}>
            <MessageCircle size={iconSize} className="text-white" />
          </div>
        );
      default:
        return null;
    }
  };

  if (!campaignDetail) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Loading campaign data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter Section */}
      <div className="flex items-center gap-4">
        <span className="font-medium text-gray-700">Filter by:</span>
        <Dropdown
          options={filterOptions.statusOptions}
          selected={filters.status}
          setSelected={(value) => setFilters(prev => ({ ...prev, status: typeof value === 'function' ? value(prev.status) : value }))}
          placeholder="Status"
          className="min-w-[140px]"
        />
        <Dropdown
          options={filterOptions.languageOptions}
          selected={filters.language}
          setSelected={(value) => setFilters(prev => ({ ...prev, language: typeof value === 'function' ? value(prev.language) : value }))}
          placeholder="Language"
          className="min-w-[140px]"
        />
        <Dropdown
          options={filterOptions.targetingOptions}
          selected={filters.targeting}
          setSelected={(value) => setFilters(prev => ({ ...prev, targeting: typeof value === 'function' ? value(prev.targeting) : value }))}
          placeholder="Targeting"
          className="min-w-[140px]"
        />
        <Dropdown
          options={filterOptions.categoryOptions}
          selected={filters.category}
          setSelected={(value) => setFilters(prev => ({ ...prev, category: typeof value === 'function' ? value(prev.category) : value }))}
          placeholder="Category"
          className="min-w-[140px]"
        />
        <Dropdown
          options={filterOptions.publisherOptions}
          selected={filters.publisher}
          setSelected={(value) => setFilters(prev => ({ ...prev, publisher: typeof value === 'function' ? value(prev.publisher) : value }))}
          placeholder="Publisher"
          className="min-w-[140px]"
        />
      </div>

      {/* Flight Sections */}
      {campaignDetail.nested_by_flight.map((flight, flightIndex) => {
        const flightKey = `flight-${flightIndex}`;
        const isExpanded = expandedFlights[flightKey];
        const assets = getFlightAssets(flight.flight_id);
        const filteredAssets = filterAssets(assets);
        const totalAssets = filteredAssets.length;

        return (
          <div key={flight.flight_id} className="rounded-xl">
            {/* Flight Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Flight {flight.flight_number}
              </h2>
              <div className="flex items-center gap-4">
                <span className="text-lg font-semibold text-gray-700">
                  {totalAssets}
                </span>
                <button
                  onClick={() => toggleFlight(flightKey)}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  <ChevronUp
                    size={20}
                    className={`transition-transform duration-200 ${
                      isExpanded ? "rotate-0" : "rotate-180"
                    }`}
                  />
                </button>
              </div>
            </div>

            <hr className="mt-2 border-1 border-gray-300 mb-2" />

            {/* Table */}
            {isExpanded && (
              <div>
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 pb-3 mb-2 text-sm font-semibold text-gray-700 border-b border-gray-300">
                  <div className="col-span-1"></div>
                  <div className="col-span-1 flex items-center gap-1">
                    Language
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                  <div className="col-span-1 flex items-center gap-1">
                    Objective
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                  <div className="col-span-1 flex items-center gap-1">
                    Region
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                  <div className="col-span-1 flex items-center gap-1">
                    Category
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                  <div className="col-span-2 flex items-center gap-1">
                    Publisher
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                  <div className="col-span-2 flex items-center gap-1">
                    Ad Type
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                  <div className="col-span-1 flex items-center gap-1">
                    Due Date
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                  <div className="col-span-2 flex items-center gap-1">
                    Status
                  </div>
                </div>

                {/* Table Body */}
                {filteredAssets.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No assets found for the selected filters
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredAssets.map((asset) => (
                      <div
                        key={asset.id}
                        className="grid grid-cols-12 gap-4 py-3 text-sm items-center hover:bg-gray-50 rounded transition-colors"
                      >
                        <div className="col-span-1">
                          <img
                            src={asset.thumbnail}
                            alt="Asset thumbnail"
                            className="w-10 h-10 rounded object-cover"
                          />
                        </div>
                        <div className="col-span-1 font-medium">{asset.language}</div>
                        <div className="col-span-1">{asset.objective}</div>
                        <div className="col-span-1">{asset.region}</div>
                        <div className="col-span-1">{asset.category}</div>
                        <div className="col-span-2">{asset.publisher}</div>
                        <div className="col-span-2">{asset.adType}</div>
                        <div className="col-span-1">{formatDate(asset.dueDate)}</div>
                        <div className="col-span-2">
                          <div className="flex items-center gap-1 flex-wrap">
                            {asset.statusIcons.slice(0, 4).map((icon, idx) =>
                              getStatusIconComponent(icon, idx)
                            )}
                            {asset.statusIcons.length > 4 && (
                              <div className="text-sm font-medium text-gray-600 ml-1">
                                +{asset.statusIcons.length - 4}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
